import { SummaryInfo } from './vo/summary-info.model';
import { ItemInfo } from './vo/item-info.model';
import { GetBuyerSelectItemsResponse } from './api-services/get-buyer-select-items.service';
import { GetCodeListResponse } from '../../../commons/kymw02/api-services/get-code-list.service';
import { GetOrderListResponse } from '../../../commons/kymw02/api-services/get-order-list.service';
import { GetNonFixedOrderListResponse } from './api-services/get-non-fixed-order-list.service';
import { GetCycleMediumSelectItemsResponse } from './api-services/get-cycle-medium-select-items.service';
import { GetCoopListResponse } from '../../../commons/kymw02/api-services/get-coop-list.service';
import { KYMW1203InputModel } from '../kymw1203/vo/kymw1203-input.model';
import { ItemEditableInfo } from '../kymw1200/vo/item-editable-info.model';
import { GetStaffBuyerSelectItemsResponse } from './api-services/get-staff-buyer-select-items.service';

// 表示モード
type DisplayMode = '0' | '1' | '2' | '3' | '4';

/**
 * サイクル設定画面の値を管理するクラス
 *
 * @export KYMW1200Model
 * @class KYMW1200Model
 */
export class KYMW1200Model {
  /**　表示切替 価格パターン */
  public static readonly PRICE_PATTERN_TYPE = '001';
  /** 表示モード バイヤー */
  public static readonly DISPLAY_MODE_BUYER: DisplayMode = '0';
  /** 表示モード 部門長 */
  public static readonly DISPLAY_MODE_LEADER: DisplayMode = '1';
  /** 表示モード 取引先 */
  public static readonly DISPLAY_MODE_SUPPLIER: DisplayMode = '2';
  /** 表示モード その他 */
  public static readonly DISPLAY_MODE_OTHERS: DisplayMode = '3';
  /** 表示モード 過去サイクル */
  public static readonly DISPLAY_MODE_PAST: DisplayMode = '4';
  /** 表示モード 過去サイクルフラグ TRUE */
  public static readonly PAST_FLAG_TRUE = '1';
  /** 表示モード 過去サイクルフラグ FALSE */
  public static readonly PAST_FLAG_FALSE = '0';

  /** バイヤー 配下全バイヤー */
  public static readonly ALL_BUYER_CODE = 'XXXXXXXX';
  public static readonly ALL_BUYER_NAME = '配下全バイヤー';
  /** バイヤー 部門配下全バイヤー */
  public static readonly CLASS_ALL_BUYER_CODE = 'YYYYYY'; // 下二桁に部門コードを繋げる
  public static readonly CLASS_ALL_BUYER_NAME = '部門:{0}の配下全バイヤー'; // {0}に部門コードを入れる

  // 表示モード
  displayMode: DisplayMode = '3';

  // ユーザ情報
  userBuyerCodeList: string[] = [];
  userLeaderClass = '';
  userSupplierCode = '';
  userId = '';

  // 検索条件
  mediumType: string;
  buyerCode: string;
  coopCode: string;
  switchType: string;
  orderCodeFrom: string;
  orderCodeTo: string;
  catalogIdFrom: string;
  catalogIdTo: string;

  // 一覧取得条件(一覧情報取得後に格納)
  searchedMediumType: string;
  searchedBuyerCode: string;
  searchedCoopCode: string;
  searchedSwitchType: string;
  searchedOrderCodeFrom: string;
  searchedOrderCodeTo: string;

  // API取得結果 選択項目など
  cycleMediumList: GetCycleMediumSelectItemsResponse = [];
  buyerList: GetBuyerSelectItemsResponse = [];
  coopList: GetCoopListResponse = [];
  switchTypeList: GetCodeListResponse = [];
  orderCodeList: GetOrderListResponse = [];
  nonFixedOrderCodeList: GetNonFixedOrderListResponse = [];
  staffBuyerList: GetStaffBuyerSelectItemsResponse = [];

  // 予実表情報
  summaryList: SummaryInfo[] = [];

  // 一覧情報
  itemList: ItemInfo[] = [];
  itemListOrigin: ItemEditableInfo[] = [];

  // ラジオ選択中受注コード(カラムヘッダ)
  radioOrderCode = '';
  // 一括設定用情報
  kymw1203in: KYMW1203InputModel;
  // 他媒体選択
  otherMediums: string[] = [];

  // 出力ファイルパス
  outputPath = '';

  /** 表示切替判定 価格パターン */
  get isPricePatternMode() {
    return KYMW1200Model.PRICE_PATTERN_TYPE === this.searchedSwitchType;
  }

  /** 表示モード判定 バイヤー */
  get isBuyer() {
    return KYMW1200Model.DISPLAY_MODE_BUYER === this.displayMode;
  }

  /** 表示モード判定 部門長 */
  get isLeader() {
    return KYMW1200Model.DISPLAY_MODE_LEADER === this.displayMode;
  }

  /** 表示モード判定 取引先 */
  get isSupplier() {
    return KYMW1200Model.DISPLAY_MODE_SUPPLIER === this.displayMode;
  }

  /** 表示モード判定 その他 */
  get isOthers() {
    return KYMW1200Model.DISPLAY_MODE_OTHERS === this.displayMode;
  }

  /** 表示モード判定 過去サイクル */
  get isPast() {
    return KYMW1200Model.DISPLAY_MODE_PAST === this.displayMode;
  }

  /** 過去サイクル (区分値) */
  get pastCycle() {
    return KYMW1200Model.DISPLAY_MODE_PAST === this.displayMode
      ? KYMW1200Model.PAST_FLAG_TRUE
      : KYMW1200Model.PAST_FLAG_FALSE;
  }

  /** 表示用 他媒体 */
  get dspOtherMediums() {
    if (this.otherMediums.length < 1) return '';
    return this.otherMediums.join(',');
  }

  /** 条件フィルタ受注コード */
  get filteredOrderList() {
    return this.orderCodeList.filter(
      _ =>
        this.searchedOrderCodeFrom <= _.OrderCode &&
        this.searchedOrderCodeTo >= _.OrderCode
    );
  }

  /** 商品単位 更新商品 (分類タグ/商品メモ) */
  get updateItemInfosPerItem() {
    return this.itemList.filter(_i => {
      const origin = this.findOrigin(_i);
      if (!origin) return false;
      return (
        _i.categoryTagId !== origin.categoryTagId ||
        _i.goodsMemo !== origin.goodsMemo
      );
    });
  }

  /** 企画回単位 更新商品 (価格パターン/企画回メモ/コピーフラグ) */
  get updateItemInfosPerCatalog() {
    return this.itemList.filter(_i => {
      const origin = this.findOrigin(_i);
      if (!origin) return false;
      return (
        Object.keys(origin.catalogInfoMap).filter(
          _k => _i.catalogInfoMap[_k].pricePattern !== origin.catalogInfoMap[_k]
        ).length > 0 ||
        Object.keys(origin.catalogMemoMap).filter(
          _k => _i.catalogInfoMap[_k].catalogMemo !== origin.catalogMemoMap[_k]
        ).length > 0 ||
        Object.keys(_i.catalogInfoMap).filter(
          _k => _i.catalogInfoMap[_k].copyCatalogMemoFlg != false
        ).length > 0
      );
    });
  }

  /** 更新企画回取得
   * origin.catalogInfoMapの企画回（サイクル確定済の企画回）で判定する
   */
  public getUpdatedCatalogIds(item: ItemInfo): string[] {
    const origin = this.findOrigin(item);
    if (!origin) return;
    return Object.keys(origin.catalogInfoMap).filter(
      _k => item.catalogInfoMap[_k].pricePattern !== origin.catalogInfoMap[_k] ||
      item.catalogInfoMap[_k].catalogMemo !== origin.catalogMemoMap[_k] ||
      item.catalogInfoMap[_k].copyCatalogMemoFlg != false
    );
  }

  /** 更新企画回取得
   * 表示している全ての企画回で判定する
   */
  public getAllUpdatedCatalogIds(item: ItemInfo): string[] {
    const origin = this.findOrigin(item);
    if (!origin) return;
    return this.filteredOrderList.filter(
      _k => item.catalogInfoMap[_k.OrderCode].pricePattern !== origin.catalogInfoMap[_k.OrderCode] ||
      item.catalogInfoMap[_k.OrderCode].catalogMemo !== origin.catalogMemoMap[_k.OrderCode] ||
      item.catalogInfoMap[_k.OrderCode].copyCatalogMemoFlg != false
    ).map(_ => _.OrderCode);
  }

  /** 検索条件クリア */
  public clearSearchedCondition() {
    this.searchedMediumType = '';
    this.searchedBuyerCode = '';
    this.searchedCoopCode = '';
    this.searchedSwitchType = '';
    this.searchedOrderCodeFrom = '';
    this.searchedOrderCodeTo = '';
  }

  /** 検索条件格納 */
  public storeSearchedCondition() {
    this.searchedMediumType = this.mediumType;
    this.searchedBuyerCode = this.buyerCode;
    this.searchedCoopCode = this.coopCode;
    this.searchedSwitchType = this.switchType;
    this.searchedOrderCodeFrom = this.orderCodeFrom;
    this.searchedOrderCodeTo = this.orderCodeTo;
  }

  /** 変更内容破棄 */
  revertItemListChanges() {
    this.itemList.forEach(_i => {
      const origin = this.findOrigin(_i);
      if (!origin) return;
      this.revertItemListInfo(_i, origin);
      this.revertCatalogInfo(_i, origin);
    });
  }
   
  /** 元の情報取得 */
  findOrigin(item) {
    return this.itemListOrigin.find(
      _o => _o.mediumType === item.mediumType && _o.pcode === item.pcode
    );
  }
   
  /** 変更内容を元に戻す */
  revertItemListInfo(item, origin) {
    item.categoryTagId = origin.categoryTagId;
    item.goodsMemo = origin.goodsMemo;
  }
   
  /** 変更内容を元に戻す(catalogInfo用) */
  revertCatalogInfo(item, origin) {
    Object.keys(origin.catalogInfoMap).forEach(_k => {
      item.catalogInfoMap[_k].pricePattern = origin.catalogInfoMap[_k];
    });
  }

  /** APIリクエスト用のバイヤーコード取得 */
  public getRequestBuyerCodes(): string {
    // 配下全バイヤー指定時はバイヤーセレクトリストの全バイヤーコード（カンマ区切り）
    if (this.buyerCode === KYMW1200Model.ALL_BUYER_CODE) {
      const buyerCodeList = [];
      for (const e of this.buyerList) {
        if (e.Code !== KYMW1200Model.ALL_BUYER_CODE && !e.Code.includes(KYMW1200Model.CLASS_ALL_BUYER_CODE)) {
          buyerCodeList.push(e.Code);
        }
      }
      return buyerCodeList.join(',');
    } else if (this.buyerCode.includes(KYMW1200Model.CLASS_ALL_BUYER_CODE)) {
      // 部門:部門コードの配下全バイヤー指定時はバイヤーセレクトリストの部門リストが部門コードと合致するバイヤーコード（カンマ区切り）
      const buyerCodeList = [];
      // 選択した部門コードを取得（バイヤーコードの下二桁が対象）
      const targetClassCode = this.buyerCode.slice(-2);
      for (const e of this.buyerList) {
        if (e.ClassCodeList && e.ClassCodeList.includes(targetClassCode)) {
          buyerCodeList.push(e.Code);
        }
      }
      return buyerCodeList.join(',');
    } else {
      return this.buyerCode;
    }
  }
}
